import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Descriptions,
    Tabs,
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    InputNumber,
    message,
    Row,
    Col,
    Divider,
    Typography
} from 'antd';
import {
    UserOutlined,
    MedicineBoxOutlined,
    FileTextOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    PrinterOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import moment from 'moment';
import DentalChart from '../../components/DentalChart/DentalChart';
import { patientService, toothHistoryService } from '../../services';
import authService from '../../services/authService';
import { useApp } from '../../context/AppContext';
import { notificationService } from '../../services';
import { getTextDirection, formatDate } from '../../utils/printUtils';
import './PatientDetail.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const PatientDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation(['patient', 'treatment', 'prescription', 'tooth', 'common', 'messages']);
    const { user } = useApp();
    const [form] = Form.useForm();

    // Check if user has permission to view patient details
    const canViewPatientDetails = user?.role === 'admin' || user?.role === 'dentist';

    const [patient, setPatient] = useState(null);
    const [dentalChart, setDentalChart] = useState([]);
    const [treatments, setTreatments] = useState([]);
    const [prescriptions, setPrescriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState(''); // 'treatment' or 'prescription'
    const [selectedTeeth, setSelectedTeeth] = useState([]);
    const [editingRecord, setEditingRecord] = useState(null);

    // Redirect if user doesn't have permission
    useEffect(() => {
        if (!canViewPatientDetails) {
            message.warning('You do not have permission to view patient details');
            navigate('/patients');
        }
    }, [canViewPatientDetails, navigate]);

    useEffect(() => {
        fetchPatientData();
    }, [id]);

    const fetchPatientData = async () => {
        setLoading(true);
        try {
            const [patientRes, chartRes, treatmentsRes, prescriptionsRes] = await Promise.all([
                patientService.getOne(id),
                toothHistoryService.getDentalChart(id),
                patientService.getTreatments(id),
                patientService.getPrescriptions(id)
            ]);

            setPatient(patientRes.data);
            setDentalChart(chartRes.data || []);
            setTreatments(treatmentsRes.data || []);
            setPrescriptions(prescriptionsRes.data || []);
        } catch (error) {
            message.error('Failed to load patient data');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddTreatment = () => {
        setModalType('treatment');
        setEditingRecord(null);
        setSelectedTeeth([]);
        form.resetFields();
        setModalVisible(true);
    };

    const handleAddPrescription = () => {
        setModalType('prescription');
        setEditingRecord(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleToothSelect = (teeth) => {
        setSelectedTeeth(teeth);
        form.setFieldsValue({ teethWorkedOn: teeth });
    };

    // Map treatment type to valid tooth condition
    const getConditionFromTreatmentType = (treatmentType) => {
        const conditionMap = {
            'Filling': 'Filled',
            'Crown': 'Crowned',
            'Extraction': 'Extracted',
            'Root Canal': 'Root Canal',
            'Cleaning': 'Healthy',
            'Whitening': 'Healthy',
            'Bridge': 'Crowned',
            'Implant': 'Healthy',
            'Braces': 'Healthy',
            'Dentures': 'Missing',
            'Veneer': 'Crowned',
            'Scaling': 'Healthy',
            'Other': 'Healthy'
        };
        return conditionMap[treatmentType] || 'Healthy';
    };

    const handleSubmit = async (values) => {
        try {
            if (modalType === 'treatment') {
                // Get current user as dentist
                const currentUser = authService.getCurrentUser();
                // Use form condition if provided, otherwise auto-detect from treatmentType
                const condition = values.condition || getConditionFromTreatmentType(values.treatmentType);

                const treatmentData = {
                    ...values,
                    patientId: id,
                    dentistId: currentUser?.id,
                    teethWorkedOn: selectedTeeth.map(t => ({
                        toothNumber: t.toothNumber,
                        toothPosition: t.position,
                        procedure: values.treatmentType,
                        condition: condition,
                        notes: values.notes
                    })),
                    date: values.date.toISOString(),
                    status: 'Completed'
                };

                let treatmentResult;
                if (editingRecord) {
                    await patientService.updateTreatment(editingRecord._id, treatmentData);
                    message.success(t('messages:updateSuccess'));
                    treatmentResult = { _id: editingRecord._id };
                } else {
                    const response = await patientService.createTreatment(treatmentData);
                    message.success(t('messages:saveSuccess'));
                    treatmentResult = response?.data || { _id: null };
                }

                // Create notification if notification date is provided
                if (values.notificationDate && !editingRecord) {
                    try {
                        const notifPayload = {
                            patientId: id,
                            treatmentId: treatmentResult._id,
                            notificationDate: values.notificationDate.toISOString(),
                            notificationType: values.notificationType || 'Follow-up',
                            priority: values.notificationPriority || 'Medium',
                            title: `Follow-up: ${values.treatmentType}`,
                            description: `Patient needs follow-up for ${values.treatmentType} treatment`
                        };
                        console.log('📅 Creating notification:', notifPayload);
                        const notifResponse = await notificationService.create(notifPayload);
                        console.log('✓ Notification created:', notifResponse);
                        message.success('✓ Notification set for ' + values.notificationDate.format('DD/MM/YYYY'));
                    } catch (notifError) {
                        console.error('❌ Error creating notification:', notifError.response?.data || notifError.message);
                        // Don't fail treatment creation if notification fails
                        message.warning('Treatment created, but notification setup failed: ' + (notifError.response?.data?.message || notifError.message));
                    }
                }

                // For Filling treatment, create notification for next refilling date
                if (values.treatmentType === 'Filling' && values.nextFillingDate && !editingRecord) {
                    try {
                        const refillPayload = {
                            patientId: id,
                            treatmentId: treatmentResult._id,
                            notificationDate: values.nextFillingDate.toISOString(),
                            notificationType: 'Filling',
                            priority: 'Medium',
                            title: `Next Refilling: ${patient.name}`,
                            description: `Patient needs refilling for tooth/teeth treatment`
                        };
                        console.log('📅 Creating refilling notification:', refillPayload);
                        const refillResponse = await notificationService.create(refillPayload);
                        console.log('✓ Refilling notification created:', refillResponse);
                        message.success('✓ Refilling reminder set for ' + values.nextFillingDate.format('DD/MM/YYYY'));
                    } catch (refillError) {
                        console.error('❌ Error creating refilling notification:', refillError.response?.data || refillError.message);
                        message.warning('Treatment created, but refilling reminder setup failed: ' + (refillError.response?.data?.message || refillError.message));
                    }
                }

                // Update tooth history
                if (selectedTeeth.length > 0) {
                    const condition = values.condition || getConditionFromTreatmentType(values.treatmentType);
                    const currentUser = authService.getCurrentUser();
                    await toothHistoryService.bulkUpdateToothHistory({
                        patientId: id,
                        treatmentId: null, // Will be set by backend if needed
                        dentistId: currentUser?.id,
                        teethWorkedOn: selectedTeeth.map(t => ({
                            toothNumber: t.toothNumber,
                            toothPosition: t.position,
                            procedure: values.treatmentType,
                            condition: condition
                        }))
                    });
                }
            } else {
                // Get current user as dentist for prescription
                const currentUser = authService.getCurrentUser();

                const prescriptionData = {
                    ...values,
                    patientId: id,
                    dentistId: currentUser?.id,
                    date: values.date.toISOString(),
                    medications: values.medications || [],
                    selectedTeeth: selectedTeeth
                };

                if (editingRecord) {
                    await patientService.updatePrescription(editingRecord._id, prescriptionData);
                    message.success(t('messages:updateSuccess'));
                } else {
                    await patientService.createPrescription(prescriptionData);
                    message.success(t('messages:saveSuccess'));
                }
            }

            setModalVisible(false);
            fetchPatientData();
        } catch (error) {
            message.error(t('messages:error'));
            console.error(error);
        }
    };

    const handlePrintPrescription = (prescription) => {
        try {
            const locale = i18n.language;
            const direction = getTextDirection(locale);
            const teethInfo = prescription.selectedTeeth || selectedTeeth;

            // Get dentist information from prescription
            const dentistName = prescription.dentistId?.name || 'Dentist';
            const dentistSpec = prescription.dentistId?.specialization || '';

            const medicationsHTML = prescription.medications && prescription.medications.length > 0
                ? prescription.medications.map(med => `
                    <tr>
                        <td>${med.name || ''}</td>
                        <td>${med.dosage || '-'}</td>
                        <td>${med.frequency || '-'}</td>
                        <td>${med.duration || '-'}</td>
                        <td>${med.instructions || '-'}</td>
                    </tr>
                `).join('')
                : '<tr><td colspan="5" style="text-align: center; color: #999;">No medications</td></tr>';

            const teethHTML = teethInfo && teethInfo.length > 0 ? `
                <div style="margin: 20px 0; padding: 15px; background: #f9f9f9; border-radius: 5px;">
                    <h3 style="margin-bottom: 10px; color: #5f7868;">${locale === 'ar' ? 'الأسنان المعالجة' : locale === 'ku' ? 'دندانە چاودێری کراو' : 'Teeth Treated'}</h3>
                    <div style="display: flex; flex-wrap: wrap; gap: 5px;">
                        ${teethInfo.map(tooth => `
                            <span style="display: inline-block; padding: 5px 10px; background: #5f7868; color: white; border-radius: 3px; font-size: 12px; font-weight: bold;">
                                ${tooth.toothNumber || tooth}
                            </span>
                        `).join('')}
                    </div>
                </div>
            ` : '';

            const htmlContent = `
                <!DOCTYPE html>
                <html dir="${direction}">
                <head>
                    <meta charset="UTF-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                    <title>${t('prescription:title') || 'Prescription'} - ${patient.name}</title>
                    <link rel="stylesheet" href="/styles/print.css">
                    <style>
                        * { margin: 0; padding: 0; box-sizing: border-box; }
                        body {
                            font-family: ${direction === 'rtl' ? "'Segoe UI', 'Arial Unicode MS'" : "'Segoe UI', Arial"}, sans-serif; 
                            padding: 30px; 
                            color: #333; 
                            max-width: 900px; 
                            margin: 0 auto; 
                        }
                        .print-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #5f7868; flex-direction: ${direction === 'rtl' ? 'row-reverse' : 'row'}; }
                        .clinic-info h1 { color: #5f7868; font-size: 24px; margin-bottom: 10px; font-weight: 600; }
                        .clinic-info p { font-size: 13px; color: #666; line-height: 1.6; margin-bottom: 5px; }
                        .print-title { text-align: ${direction === 'rtl' ? 'left' : 'right'}; }
                        .print-title h2 { color: #5f7868; font-size: 28px; margin-bottom: 5px; font-weight: 600; }
                        .print-title p { font-size: 13px; color: #666; margin: 3px 0; }
                        .info-section { display: flex; justify-content: space-between; margin-bottom: 25px; gap: 20px; flex-direction: ${direction === 'rtl' ? 'row-reverse' : 'row'}; }
                        .info-box { flex: 1; text-align: ${direction === 'rtl' ? 'right' : 'left'}; }
                        .info-box h3 { font-size: 12px; text-transform: uppercase; color: #5f7868; margin-bottom: 10px; letter-spacing: 1px; font-weight: 600; border-bottom: 1px solid #e0e0e0; padding-bottom: 5px; }
                        .info-box p { font-size: 14px; line-height: 1.8; margin-bottom: 3px; }
                        .info-box strong { font-weight: 600; color: #333; }
                        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; background: white; }
                        th { background-color: #5f7868; color: white; padding: 12px; text-align: ${direction === 'rtl' ? 'right' : 'left'}; font-size: 13px; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; }
                        td { border-bottom: 1px solid #eee; padding: 12px; font-size: 14px; text-align: ${direction === 'rtl' ? 'right' : 'left'}; }
                        tr:nth-child(even) { background-color: #f9f9f9; }
                        .signature-section { margin-top: 40px; display: flex; justify-content: ${direction === 'rtl' ? 'flex-start' : 'flex-end'}; gap: 40px; flex-direction: ${direction === 'rtl' ? 'row-reverse' : 'row'}; }
                        .signature-box { width: 200px; text-align: center; }
                        .signature-line { border-bottom: 1px solid #333; margin-bottom: 5px; height: 50px; }
                        .signature-label { font-size: 13px; color: #666; font-weight: 600; line-height: 1.6; }
                        .print-footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: ${direction === 'rtl' ? 'right' : 'center'}; font-size: 12px; color: #999; }
                        @media print { body { padding: 15px; max-width: 100%; } .no-print { display: none !important; } }
                    </style>
                </head>
                <body>
                    <div class="print-header">
                        <div class="clinic-info">
                            <h1>${t('common:clinic') || 'Dental Clinic'}</h1>
                        </div>
                        <div class="print-title">
                            <h2>🦷 ${t('prescription:title') || 'PRESCRIPTION'}</h2>
                            <p><strong>${t('print:date')}:</strong> ${formatDate(new Date(), locale)}</p>
                        </div>
                    </div>

                    <div class="info-section">
                        <div class="info-box">
                            <h3>${locale === 'ar' ? 'معلومات المريض' : locale === 'ku' ? 'زانیاری نەخۆش' : 'PATIENT INFORMATION'}</h3>
                            <p><strong>${patient?.name || 'N/A'}</strong></p>
                            ${patient?.age ? `<p><strong>${t('print:patientAge')}:</strong> ${patient.age}</p>` : ''}
                            ${patient?.contact ? `<p><strong>${t('print:phone')}:</strong> ${patient.contact}</p>` : ''}
                            <p><strong>${t('print:date')}:</strong> ${formatDate(prescription.date, locale)}</p>
                        </div>
                        <div class="info-box">
                            <h3>${locale === 'ar' ? 'طبيب الأسنان' : locale === 'ku' ? 'دندانپزشک' : 'DENTIST'}</h3>
                            <p><strong>${dentistName}</strong></p>
                            ${dentistSpec ? `<p>${dentistSpec}</p>` : ''}
                            ${prescription.dentistId?.phone ? `<p><strong>${t('print:phone')}:</strong> ${prescription.dentistId.phone}</p>` : ''}
                        </div>
                    </div>

                    ${teethHTML}

                    <div style="margin: 20px 0;">
                        <h3 style="color: #5f7868; margin-bottom: 15px; font-size: 14px; text-transform: uppercase; letter-spacing: 1px; font-weight: 600;">
                            ${locale === 'ar' ? 'الأدوية' : locale === 'ku' ? 'دەرمان' : 'MEDICATIONS'}
                        </h3>
                        <table>
                            <thead>
                                <tr>
                                    <th>${locale === 'ar' ? 'الدواء' : locale === 'ku' ? 'دەرمان' : 'Medicine'}</th>
                                    <th>${locale === 'ar' ? 'الجرعة' : locale === 'ku' ? 'بەرھەم' : 'Dosage'}</th>
                                    <th>${locale === 'ar' ? 'التكرار' : locale === 'ku' ? 'جار' : 'Frequency'}</th>
                                    <th>${locale === 'ar' ? 'المدة' : locale === 'ku' ? 'مەودا' : 'Duration'}</th>
                                    <th>${locale === 'ar' ? 'التعليمات' : locale === 'ku' ? 'ڕێنیونی' : 'Instructions'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${medicationsHTML}
                            </tbody>
                        </table>
                    </div>

                    ${prescription.generalInstructions ? `
                    <div style="margin: 20px 0; padding: 15px; background: #f9f9f9; border-radius: 5px;">
                        <h3 style="color: #5f7868; margin-bottom: 10px; font-size: 14px; text-transform: uppercase; letter-spacing: 1px; font-weight: 600;">
                            ${locale === 'ar' ? 'التعليمات العامة' : locale === 'ku' ? 'ڕێنیونی گشتی' : 'GENERAL INSTRUCTIONS'}
                        </h3>
                        <p>${prescription.generalInstructions}</p>
                    </div>
                    ` : ''}

                    <div class="signature-section">
                        <div class="signature-box">
                            <div class="signature-line"></div>
                            <div class="signature-label">
                                <strong>Dr. ${dentistName}</strong><br>
                                ${dentistSpec || ''}
                            </div>
                        </div>
                    </div>

                    <div class="print-footer">
                        <p>${formatDate(new Date(), locale)}</p>
                    </div>
                </body>
                </html>
            `;

            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                message.error(t('print:printBlocked') || 'Please allow popups to print');
                return;
            }

            printWindow.document.write(htmlContent);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
            }, 300);

            message.success(t('print:printSuccess') || 'Prescription sent to printer');
        } catch (error) {
            message.error(t('print:printError') || 'Failed to print prescription');
            console.error('Print error:', error);
        }
    };

    const treatmentColumns = [
        {
            title: t('treatment:type'),
            dataIndex: 'treatmentType',
            key: 'treatmentType'
        },
        {
            title: t('treatment:date'),
            dataIndex: 'date',
            key: 'date',
            render: (date) => moment(date).format('DD/MM/YYYY')
        },
        {
            title: t('treatment:dentist'),
            dataIndex: ['dentistId', 'name'],
            key: 'dentist'
        },
        {
            title: t('treatment:cost'),
            dataIndex: 'cost',
            key: 'cost',
            render: (cost) => `$${cost}`
        },
        {
            title: t('common:status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'Completed' ? 'green' : 'blue'}>{status}</Tag>
            )
        },
        {
            title: t('common:actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => handleEditTreatment(record)} />
                    <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteTreatment(record._id)} />
                </Space>
            )
        }
    ];

    const prescriptionColumns = [
        {
            title: t('prescription:number'),
            dataIndex: 'prescriptionNumber',
            key: 'prescriptionNumber'
        },
        {
            title: t('prescription:date'),
            dataIndex: 'date',
            key: 'date',
            render: (date) => moment(date).format('DD/MM/YYYY')
        },
        {
            title: t('prescription:dentist'),
            dataIndex: ['dentistId', 'name'],
            key: 'dentist'
        },
        {
            title: t('prescription:medications'),
            dataIndex: 'medications',
            key: 'medications',
            render: (medications) => medications?.length || 0
        },
        {
            title: t('common:actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<PrinterOutlined />} size="small" onClick={() => handlePrintPrescription(record)} />
                    <Button icon={<EditOutlined />} size="small" onClick={() => handleEditPrescription(record)} />
                    <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeletePrescription(record._id)} />
                </Space>
            )
        }
    ];

    const handleEditTreatment = (record) => {
        setEditingRecord(record);
        setModalType('treatment');
        setSelectedTeeth(record.teethWorkedOn || []);
        form.setFieldsValue({
            ...record,
            date: moment(record.date)
        });
        setModalVisible(true);
    };

    const handleEditPrescription = (record) => {
        setEditingRecord(record);
        setModalType('prescription');
        form.setFieldsValue({
            ...record,
            date: moment(record.date)
        });
        setModalVisible(true);
    };

    const handleDeleteTreatment = async (id) => {
        Modal.confirm({
            title: t('common:confirmDelete'),
            onOk: async () => {
                try {
                    await patientService.deleteTreatment(id);
                    message.success(t('messages:deleted'));
                    fetchPatientData();
                } catch (error) {
                    message.error(t('messages:error'));
                }
            }
        });
    };

    const handleDeletePrescription = async (id) => {
        Modal.confirm({
            title: t('common:confirmDelete'),
            onOk: async () => {
                try {
                    await patientService.deletePrescription(id);
                    message.success(t('messages:deleted'));
                    fetchPatientData();
                } catch (error) {
                    message.error(t('messages:error'));
                }
            }
        });
    };

    // Redirect if user doesn't have permission
    if (!canViewPatientDetails) {
        return null;
    }

    if (loading || !patient) {
        return <div>{t('common:loading')}</div>;
    }

    return (
        <div className="patient-detail">
            <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/patients')}
                style={{ marginBottom: 16 }}
            >
                {t('common:back')}
            </Button>

            <Card>
                <Row gutter={16}>
                    <Col span={4}>
                        <div className="patient-avatar">
                            <UserOutlined style={{ fontSize: 64 }} />
                        </div>
                    </Col>
                    <Col span={20}>
                        <Title level={2}>{patient.name}</Title>
                        <Descriptions column={3}>
                            <Descriptions.Item label={t('patient:patientId')}>{patient.patientId}</Descriptions.Item>
                            <Descriptions.Item label={t('patient:age')}>{patient.age} years</Descriptions.Item>
                            <Descriptions.Item label={t('patient:gender')}>{patient.gender}</Descriptions.Item>
                            <Descriptions.Item label={t('patient:contact')}>{patient.contact}</Descriptions.Item>
                            <Descriptions.Item label={t('patient:email')}>{patient.email || '-'}</Descriptions.Item>
                            <Descriptions.Item label={t('patient:bloodType')}>
                                {patient.medicalHistory?.bloodType || 'Unknown'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                </Row>
            </Card>

            <Card style={{ marginTop: 16 }}>
                <Tabs defaultActiveKey="dental-chart">
                    <TabPane
                        tab={<span><MedicineBoxOutlined /> {t('patient:dentalChart')}</span>}
                        key="dental-chart"
                    >
                        <DentalChart
                            patientId={id}
                            treatments={dentalChart}
                            onToothClick={(tooth) => console.log('Tooth clicked:', tooth)}
                        />
                    </TabPane>

                    <TabPane
                        tab={<span><MedicineBoxOutlined /> {t('treatment:title')}</span>}
                        key="treatments"
                    >
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAddTreatment}
                            style={{ marginBottom: 16 }}
                        >
                            {t('treatment:add')}
                        </Button>
                        <Table
                            columns={treatmentColumns}
                            dataSource={treatments}
                            rowKey="_id"
                            pagination={{ pageSize: 10 }}
                            scroll={{ x: 'max-content' }}
                        />
                    </TabPane>

                    <TabPane
                        tab={<span><FileTextOutlined /> {t('prescription:title')}</span>}
                        key="prescriptions"
                    >
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAddPrescription}
                            style={{ marginBottom: 16 }}
                        >
                            {t('prescription:add')}
                        </Button>
                        <Table
                            columns={prescriptionColumns}
                            dataSource={prescriptions}
                            rowKey="_id"
                            pagination={{ pageSize: 10 }}
                            scroll={{ x: 'max-content' }}
                        />
                    </TabPane>
                </Tabs>
            </Card>

            <Modal
                title={modalType === 'treatment' ? t('treatment:add') : t('prescription:add')}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={() => form.submit()}
                width={800}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {modalType === 'treatment' ? (
                        <>
                            <Form.Item name="treatmentType" label={t('treatment:type')} rules={[{ required: true }]}>
                                <Select>
                                    <Select.Option value="Cleaning">{t('treatment:types.cleaning')}</Select.Option>
                                    <Select.Option value="Filling">{t('treatment:types.filling')}</Select.Option>
                                    <Select.Option value="Root Canal">{t('treatment:types.rootCanal')}</Select.Option>
                                    <Select.Option value="Extraction">{t('treatment:types.extraction')}</Select.Option>
                                    <Select.Option value="Crown">{t('treatment:types.crown')}</Select.Option>
                                    <Select.Option value="Whitening">{t('treatment:types.whitening')}</Select.Option>
                                    <Select.Option value="Bridge">{t('treatment:types.bridge')}</Select.Option>
                                    <Select.Option value="Implant">{t('treatment:types.implant')}</Select.Option>
                                    <Select.Option value="Scaling">{t('treatment:types.scaling')}</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item name="condition" label={t('tooth:condition')} help={t('treatment:conditionHelp', 'Auto-detected from treatment type, can be overridden')}>
                                <Select placeholder={t('treatment:autoDetect', 'Auto-detect from treatment type')}>
                                    <Select.Option value="Healthy">{t('tooth:healthy')}</Select.Option>
                                    <Select.Option value="Cavity">{t('tooth:cavity')}</Select.Option>
                                    <Select.Option value="Filled">{t('tooth:filled')}</Select.Option>
                                    <Select.Option value="Crowned">{t('tooth:crowned')}</Select.Option>
                                    <Select.Option value="Missing">{t('tooth:missing')}</Select.Option>
                                    <Select.Option value="Root Canal">{t('tooth:rootCanal')}</Select.Option>
                                    <Select.Option value="Extracted">{t('tooth:extracted')}</Select.Option>
                                    <Select.Option value="Infected">{t('tooth:infected')}</Select.Option>
                                    <Select.Option value="Damaged">{t('tooth:damaged')}</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label={t('treatment:selectTeeth')}>
                                <DentalChart
                                    patientId={id}
                                    treatments={dentalChart}
                                    selectionMode={true}
                                    selectedTeeth={selectedTeeth}
                                    onSelectionChange={handleToothSelect}
                                />
                            </Form.Item>

                            <Form.Item name="description" label={t('treatment:description')}>
                                <TextArea rows={3} />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="cost" label={t('treatment:cost')} rules={[{ required: true }]}>
                                        <InputNumber style={{ width: '100%' }} min={0} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="date" label={t('treatment:date')} rules={[{ required: true }]}>
                                        <DatePicker style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item name="notes" label={t('treatment:notes')}>
                                <TextArea rows={2} />
                            </Form.Item>

                            <Divider>Reminders & Notifications (Optional)</Divider>

                            {/* For Filling treatments, show next refilling date */}
                            {form.getFieldValue('treatmentType') === 'Filling' && (
                                <Form.Item name="nextFillingDate" label="Next Refilling Date"
                                    help="Optional: When does this patient need to come back for refilling?">
                                    <DatePicker style={{ width: '100%' }} placeholder="Select refilling date" />
                                </Form.Item>
                            )}

                            <Form.Item name="notificationDate" label="General Follow-up Date"
                                help="Optional: Select a date for general follow-up reminder">
                                <DatePicker style={{ width: '100%' }} placeholder="Leave blank if not needed" />
                            </Form.Item>

                            {form.getFieldValue('notificationDate') && (
                                <>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name="notificationType" label="Reminder Type" initialValue="Follow-up">
                                                <Select>
                                                    <Select.Option value="Follow-up">Follow-up</Select.Option>
                                                    <Select.Option value="Filling">Filling Check</Select.Option>
                                                    <Select.Option value="Cleaning">Cleaning</Select.Option>
                                                    <Select.Option value="Root Canal">Root Canal</Select.Option>
                                                    <Select.Option value="Crown">Crown</Select.Option>
                                                    <Select.Option value="Extraction">Extraction Check</Select.Option>
                                                    <Select.Option value="Other">Other</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name="notificationPriority" label="Priority" initialValue="Medium">
                                                <Select>
                                                    <Select.Option value="Low">Low</Select.Option>
                                                    <Select.Option value="Medium">Medium</Select.Option>
                                                    <Select.Option value="High">High</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <Form.Item name="diagnosis" label={t('prescription:diagnosis')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>

                            <Form.Item name="date" label={t('prescription:date')} rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.List name="medications">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(field => (
                                            <Card key={field.key} size="small" style={{ marginBottom: 8 }}>
                                                <Row gutter={16}>
                                                    <Col span={12}>
                                                        <Form.Item {...field} name={[field.name, 'name']} label={t('prescription:medicine')} rules={[{ required: true }]}>
                                                            <Input />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Form.Item {...field} name={[field.name, 'dosage']} label={t('prescription:dosage')} rules={[{ required: true }]}>
                                                            <Input />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Form.Item {...field} name={[field.name, 'frequency']} label={t('prescription:frequency')}>
                                                            <Input />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Form.Item {...field} name={[field.name, 'duration']} label={t('prescription:duration')}>
                                                            <Input />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={24}>
                                                        <Form.Item {...field} name={[field.name, 'instructions']} label={t('prescription:instructions')}>
                                                            <Input />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Button danger onClick={() => remove(field.name)}>{t('prescription:remove')}</Button>
                                            </Card>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            {t('prescription:addMedication')}
                                        </Button>
                                    </>
                                )}
                            </Form.List>

                            <Form.Item name="generalInstructions" label={t('prescription:instructions')} style={{ marginTop: 16 }}>
                                <TextArea rows={3} />
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default PatientDetail;
